import { pickSelectedHref } from '../nav-match.util';
import type { SidebarNavAccess } from '../use-sidebar-nav-access.hook';
import type { ChatTab } from './chat-tabs.util';
import {
  coveredPaths,
  destinationState,
  isSettingsPath,
  SIDEBAR_DESTINATIONS,
  type SidebarPageId,
} from './sidebar-destinations';
import {
  pinnedChatIds,
  refKey,
  type SidebarLayout,
} from './sidebar-layout.util';

/**
 * What the page rail (the folded tabbed sidebar) shows, as plain data: the
 * pages in named sections, the page the screen selects, and the pinned
 * chats.
 */

/**
 * A page on the rail. The extension's installer has none: the tabbed shell
 * keeps it as a tab of Integrations.
 */
export interface RailPage {
  /** Unique on the rail: the page's ref key. */
  key: string;
  /** The page, for the locked click. */
  pageId: SidebarPageId;
  label: string;
  /** A DynamicIcon name. */
  iconName: string;
  href: string;
  /** Match only this path, not its detail routes. */
  exact?: boolean;
  /** Other paths the page covers. */
  also?: readonly string[];
  locked: boolean;
  /** The note a locked page shows. */
  lockedTooltip?: string;
}

export type RailSectionId =
  | 'home'
  | 'prospect'
  | 'engage'
  | 'automate'
  | 'insights';

export interface RailSection {
  id: RailSectionId;
  /** The flyout's title, and the cell's name. */
  label: string;
  /** The cell's icon, where the section has more than one page. */
  iconName: string;
  pages: RailPage[];
}

const SECTIONS: {
  id: RailSectionId;
  label: string;
  iconName: string;
  pages: SidebarPageId[];
}[] = [
  { id: 'home', label: 'Home', iconName: 'house', pages: ['home'] },
  {
    id: 'prospect',
    label: 'Prospect & Enrich',
    iconName: 'bullseye-arrow',
    pages: ['find-people', 'lead-lists', 'people', 'companies'],
  },
  {
    id: 'engage',
    label: 'Engage',
    iconName: 'envelope',
    pages: ['sequences', 'inbox', 'tasks'],
  },
  {
    id: 'automate',
    label: 'Automate',
    iconName: 'bolt',
    pages: ['agents', 'integrations'],
  },
  {
    id: 'insights',
    label: 'Insights',
    iconName: 'chart-user',
    pages: ['reports'],
  },
];

function railPage(
  id: SidebarPageId,
  access: SidebarNavAccess
): RailPage | null {
  const { visible, locked } = destinationState(id, access);
  if (!visible) return null;
  const page = SIDEBAR_DESTINATIONS[id];
  return {
    key: refKey({ kind: 'page', id }),
    pageId: id,
    label: page.label,
    iconName: page.iconName,
    href: page.href,
    exact: page.exact,
    // People and Companies are tabs on Lead Lists. Where they are pages of
    // their own, Lead Lists covers only its own paths.
    also: id === 'lead-lists' && access.showDirectory ? undefined : page.also,
    locked,
    lockedTooltip: locked ? page.lockedTooltip : undefined,
  };
}

/**
 * The sections, each with the pages this user can see under the rules the
 * sidebar applies to the same page everywhere (see destinationState). A
 * section left with no pages is left out. Settings is not on the rail: the
 * footer's gear goes there.
 */
export function railSections(access: SidebarNavAccess): RailSection[] {
  return SECTIONS.flatMap(({ pages: ids, ...section }) => {
    const pages = ids.flatMap((id) => railPage(id, access) ?? []);
    return pages.length > 0 ? [{ ...section, pages }] : [];
  });
}

/**
 * The page on screen: the most specific page covering the path, as the
 * router marks its link current. None on a Settings page, where the footer's
 * gear is selected.
 */
export function selectedRailKey(
  sections: readonly RailSection[],
  pathname: string
): string | null {
  if (isSettingsPath(pathname)) return null;
  const pages = sections.flatMap((section) => section.pages);
  const paths = coveredPaths(pages);
  const href = pickSelectedHref(pathname, paths);
  return paths.find((path) => path.href === href)?.key ?? null;
}

/** The pinned chats still there, in the order the layout holds them. */
export function pinnedChatList(
  layout: SidebarLayout,
  pinned: ReadonlyMap<string, ChatTab>
): ChatTab[] {
  return pinnedChatIds(layout).flatMap((id) => pinned.get(id) ?? []);
}
